<template>
  <div class="d-flex flex-column gap-4 w-75">
    <p class="font-weight-medium">Work the customer wants done</p>
    <div class="d-flex flex-column gap-2">
      <SoCheckbox
        v-for="extra in extraOptions"
        :key="extra.value"
        v-model="jobNotes.extras"
        :value="extra.value"
        :label="extra.label"
        multiple
        data-testid="extra-checkbox"
      />
    </div>

    <p class="font-weight-medium mt-2">Smart meter upgrade</p>
    <SoRadioCards v-model="jobNotes.jobSelection">
      <div class="d-flex flex-column gap-4">
        <SoRadioCard
          v-for="appointmentType in appointmentTypes"
          :key="appointmentType.value"
          :value="appointmentType.value"
          :disabled="appointmentType.disabled"
          data-testid="appointment-type-card"
        >
          <template #title>{{ appointmentType.title }}</template>
          {{ appointmentType.description }}
        </SoRadioCard>
      </div>
    </SoRadioCards>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoRadioCards from "@/components/SoRadioCards.vue"
  import SoRadioCard from "@/components/SoRadioCard.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import { APPOINTMENT_TYPE_TITLES } from "@/types/ImbpAppointmentTypes"
  import {
    ImbpExtra,
    ImbpJobNotes,
  } from "@/flows/useCases/imbpBookingNew/types"

  interface AppointmentType {
    value: string
    title: string
    description: string
    disabled?: boolean
  }

  const EXTRAS: ImbpExtra[] = ["ISOLATION_SWITCH", "METER_TAILS"]

  const EXTRA_NOTE_TEXT: Record<ImbpExtra, string> = {
    ISOLATION_SWITCH: "an isolation switch",
    METER_TAILS: "meter tails",
  }

  function isExtra(value: string | null): value is ImbpExtra {
    return value !== null && (EXTRAS as readonly string[]).includes(value)
  }

  // On a first visit, tick the extra picked on the previous step. When the agent comes
  // back to this step, keep what they ticked, unless they have since picked a different
  // extra on the previous step. By then appointmentType may already be SMART_METER_UPGRADE.
  function initialExtras(
    selectedAppointmentType: string | null,
    savedExtras: ImbpExtra[] | undefined,
  ): ImbpExtra[] {
    const saved = (savedExtras ?? []).filter(isExtra)
    if (!isExtra(selectedAppointmentType)) return saved
    return saved.includes(selectedAppointmentType)
      ? saved
      : [selectedAppointmentType]
  }

  export default defineComponent({
    components: {
      SoRadioCards,
      SoRadioCard,
      SoCheckbox,
    },
    props: {
      modelValue: {
        type: Object as PropType<Partial<ImbpJobNotes>>,
        default: () => ({}),
      },
      selectedAppointmentType: {
        type: String as PropType<string | null>,
        default: null,
      },
      hasComplexMeterSetup: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["update:model-value"],
    data() {
      return {
        jobNotes: {
          jobSelection: (this.modelValue.jobSelection ?? null) as string | null,
          extras: initialExtras(
            this.selectedAppointmentType,
            this.modelValue.extras,
          ),
          note: {
            question: "Important to note re the job?",
            answer: "",
          },
        },
      }
    },
    computed: {
      extraOptions() {
        return EXTRAS.map((extra) => ({
          value: extra,
          label: APPOINTMENT_TYPE_TITLES[extra],
        }))
      },
      selectedExtras(): ImbpExtra[] {
        return EXTRAS.filter((extra) => this.jobNotes.extras.includes(extra))
      },
      selectedExtrasText(): string {
        const names = this.selectedExtras.map((extra) =>
          APPOINTMENT_TYPE_TITLES[extra].toLowerCase(),
        )
        return names.length > 0 ? names.join(" and ") : "selected work"
      },
      appointmentTypes(): AppointmentType[] {
        return [
          {
            value: "SMART_METER_UPGRADE",
            title: "Include smart meter upgrade (Recommended)",
            description: `We can upgrade the customer's meters during the same appointment. The upgrade and the ${this.selectedExtrasText} are free when done together.`,
            disabled: this.hasComplexMeterSetup,
          },
          {
            value: "SITEWORKS",
            title: "Continue without smart meter upgrade",
            description: `Siteworks will quote for and book the ${this.selectedExtrasText}. This is chargeable without the upgrade.`,
          },
        ]
      },
      noteAnswer(): string {
        if (
          this.jobNotes.jobSelection !== "SMART_METER_UPGRADE" ||
          this.selectedExtras.length === 0
        ) {
          return ""
        }
        const work = this.selectedExtras
          .map((extra) => EXTRA_NOTE_TEXT[extra])
          .join(" and ")
        return `Please install ${work} as part of the job`
      },
    },
    watch: {
      noteAnswer: {
        handler(answer: string) {
          this.jobNotes.note.answer = answer
        },
        immediate: true,
      },
      jobNotes: {
        handler(newValue) {
          this.$emit("update:model-value", newValue)
        },
        deep: true,
        immediate: true,
      },
    },
  })
</script>
